import { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { MediaUploadField } from "./MediaUploadField";

// Generic field-based editor modal for verticals/blog/news/jobs.
export function CrudEditor({ coll, fields, initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    const base = {};
    fields.forEach((f) => {
      const v = initial ? initial[f.name] : undefined;
      base[f.name] = v !== undefined && v !== null ? v : (f.type === "paragraphs" ? "" : f.type === "bool" ? true : "");
    });
    // paragraphs stored as arrays -> join for editing
    fields.forEach((f) => {
      if (f.type === "paragraphs" && Array.isArray(base[f.name])) base[f.name] = base[f.name].join("\n\n");
    });
    return base;
  });
  const [saving, setSaving] = useState(false);

  const setVal = (name, val) => setForm((s) => ({ ...s, [name]: val }));

  const save = async () => {
    setSaving(true);
    const payload = {};
    fields.forEach((f) => {
      if (f.type === "paragraphs") {
        payload[f.name] = String(form[f.name] || "").split(/\n{2,}/).map((s) => s.trim()).filter(Boolean);
      } else if (f.type === "number") {
        payload[f.name] = Number(form[f.name] || 0);
      } else if (f.type === "bool") {
        payload[f.name] = !!form[f.name];
      } else {
        payload[f.name] = form[f.name] ?? "";
      }
    });
    try {
      if (initial && initial.id) {
        await api.put(`/admin/${coll}/${initial.id}`, payload);
      } else {
        await api.post(`/admin/${coll}`, payload);
      }
      toast.success("Saved");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-10 px-4" data-testid="crud-editor-modal">
      <div className="bg-[#0B0F17] border border-slate-800 rounded-2xl w-full max-w-2xl">
        <div className="flex items-center justify-between p-6 border-b border-slate-800 sticky top-0 bg-[#0B0F17] rounded-t-2xl">
          <h3 className="font-display font-bold text-white text-lg">
            {initial ? "Edit" : "New"} {coll.slice(0, 1).toUpperCase() + coll.slice(1, coll.length - 1)}
          </h3>
          <button onClick={onClose} data-testid="crud-editor-close" className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          {fields.map((f) => (
            <div key={f.name}>
              <label className="text-sm text-slate-400 mb-1.5 block">{f.label}</label>
              {f.type === "paragraphs" ? (
                <textarea rows={f.rows || 6} value={form[f.name]} onChange={(e) => setVal(f.name, e.target.value)}
                  data-testid={`crud-field-${f.name}`}
                  placeholder="Separate paragraphs with a blank line"
                  className="w-full bg-[#070b12] border border-slate-800 rounded-lg px-4 py-3 text-slate-200 focus:outline-none focus:border-emerald-500/50" />
              ) : f.type === "bool" ? (
                <label className="flex items-center gap-2 text-slate-300">
                  <input type="checkbox" checked={!!form[f.name]} onChange={(e) => setVal(f.name, e.target.checked)} data-testid={`crud-field-${f.name}`} />
                  Published
                </label>
              ) : f.type === "image" ? (
                <MediaUploadField value={form[f.name]} onChange={(url) => setVal(f.name, url)} testid={`crud-field-${f.name}`} />
              ) : f.type === "select" ? (
                <select value={form[f.name]} onChange={(e) => setVal(f.name, e.target.value)} data-testid={`crud-field-${f.name}`}
                  className="w-full bg-[#070b12] border border-slate-800 rounded-lg px-4 py-3 text-slate-200 focus:outline-none focus:border-emerald-500/50">
                  {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input type={f.type === "number" ? "number" : "text"} value={form[f.name]} onChange={(e) => setVal(f.name, e.target.value)}
                  data-testid={`crud-field-${f.name}`}
                  className="w-full bg-[#070b12] border border-slate-800 rounded-lg px-4 py-3 text-slate-200 focus:outline-none focus:border-emerald-500/50" />
              )}
            </div>
          ))}
        </div>
        <div className="p-6 border-t border-slate-800 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 rounded-lg border border-slate-700 text-slate-300">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="crud-editor-save" className="btn-emerald px-6 py-2.5 rounded-lg disabled:opacity-60">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
